import {
  createComboboxPlugin,
  createPlateEditor,
  CreatePlateEditorOptions,
  createPlugins as createPlatePlugins,
  PlatePlugin,
} from '@udecode/plate';
import {
  SERVICE_FACEBOOK,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  SERVICE_MASTODON,
  SERVICE_PINTEREST,
  SERVICE_STARTPAGE,
  SERVICE_TIKTOK,
  SERVICE_TWITTER,
  SERVICE_YOUTUBE,
} from '~/constants';
import { ServiceDefinition } from '~/constants/services/types';
import { BufferEditorOptions } from '~/editor/BufferEditor/types';
import { BufferEditor, BufferValue } from '~/editor/BufferEditor/types.plate';
import { createCharacterLimitHighlightPlugin } from '~/editor/plugins/character-limit-highlight';
import { createEmojiSearchPlugin } from '~/editor/plugins/emoji';
import { createErrorReportingPlugin } from '~/editor/plugins/error-reporting/createErrorReportingPlugin';
import { createFacebookMentionPlugin } from '~/editor/plugins/facebook-mention';
import { createLinkPlugin } from '~/editor/plugins/link';
import { createLinkedInAnnotationsPlugin } from '~/editor/plugins/linkedin-annotations/createLinkedInAnnotationsPlugin';
import { createParagraphPlugin } from '~/editor/plugins/paragraph';
import { createTwitterHashtagPlugin } from '~/editor/plugins/twitter-hashtag';
import { createTwitterMentionPlugin } from '~/editor/plugins/twitter-mention';
import { createGenerateContentPlugin } from '~/editor/plugins/generate-content';
import { BufferPluginOptions } from '~/editor/plugins/types';

const createPlugins = (
  plugins: PlatePlugin<BufferPluginOptions, BufferValue, BufferEditor>[] = []
) => {
  return createPlatePlugins<BufferValue, BufferEditor>([
    createParagraphPlugin(),
    createComboboxPlugin(),
    createEmojiSearchPlugin(),
    createCharacterLimitHighlightPlugin(),
    createGenerateContentPlugin(),
    createLinkPlugin(),
    ...plugins,
    createErrorReportingPlugin(),
  ]);
};

export const createTwitterEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_TWITTER,
    plugins: createPlugins([
      createTwitterMentionPlugin(),
      createTwitterHashtagPlugin(),
    ]),
    ...options,
  });
};

export const createInstagramEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_INSTAGRAM,
    plugins: createPlugins(),
    ...options,
  });
};

export const createLinkedInEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_LINKEDIN,
    plugins: createPlugins([createLinkedInAnnotationsPlugin()]),
    ...options,
  });
};

export const createFacebookEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_FACEBOOK,
    plugins: createPlugins([createFacebookMentionPlugin()]),
    ...options,
  });
};

export const createPinterestEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_PINTEREST,
    plugins: createPlugins(),
    ...options,
  });
};

export const createMastodonEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_MASTODON,
    plugins: createPlugins(),
    ...options,
  });
};

export const createYoutubeEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_YOUTUBE,
    plugins: createPlugins(),
    ...options,
  });
};

export const createTiktokEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_TIKTOK,
    plugins: createPlugins(),
    ...options,
  });
};

export const createGooglebusinessEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_GOOGLEBUSINESS,
    plugins: createPlugins(),
    ...options,
  });
};

export const createStartPageEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: SERVICE_STARTPAGE,
    plugins: createPlugins(),
    ...options,
  });
};

export const createOmniEditor = (
  options: CreatePlateEditorOptions<BufferValue, BufferEditor> = {}
) => {
  return createPlateEditor<BufferValue, BufferEditor>({
    id: 'omni',
    plugins: createPlugins(),
    ...options,
  });
};

export const createEditorForService = (
  service: ServiceDefinition,
  options?: BufferEditorOptions
): BufferEditor => {
  switch (service.name) {
    case SERVICE_TWITTER:
      return createTwitterEditor(options);
    case SERVICE_INSTAGRAM:
      return createInstagramEditor(options);
    case SERVICE_LINKEDIN:
      return createLinkedInEditor(options);
    case SERVICE_FACEBOOK:
      return createFacebookEditor(options);
    case SERVICE_PINTEREST:
      return createPinterestEditor(options);
    case SERVICE_TIKTOK:
      return createTiktokEditor(options);
    case SERVICE_GOOGLEBUSINESS:
      return createGooglebusinessEditor(options);
    case SERVICE_STARTPAGE:
      return createStartPageEditor(options);
    case SERVICE_MASTODON:
      return createMastodonEditor(options);
    case SERVICE_YOUTUBE:
      return createYoutubeEditor(options);
    case 'omni':
      return createOmniEditor(options);
    default:
      throw new Error(
        `Invalid service: Could not create editor for service: ${service?.name}`
      );
  }
};

export const createNewEditor = (
  service: ServiceDefinition,
  options?: BufferEditorOptions
) => {
  return createEditorForService(service, options);
};
